<template>
  <div class="lp-auction-info">
    <div class="listing-body__container--header">
      <q-icon class="listing-body__icon--header" name="auction_round" />
      <span class="listing-body__text--header" v-text="t('section.auction.title')" />
    </div>

    <div class="lp-auction-info__subtitle" v-text="t('section.auction.subtitle')" />

    <div class="lp-auction-info__container">
      <div class="lp-auction-info__round-content">
        <div class="lp-auction-info__round-content-item">
          <div class="lp-auction-info__round-content-item-content">
            <span
              class="lp-auction-info__round-content-label"
              v-text="t('section.auction.currentRound')"
            />
            <span
              class="lp-auction-info__round-content-value"
              v-text="ordinal(currentRound, $i18n.locale)"
            />
          </div>
          <q-separator />
        </div>
      </div>

      <div>
        <div class="lp-auction-info__tabs-container">
          <q-tabs
            v-for="auctionRound in auctionRounds"
            :key="auctionRound.number"
            v-model="tab"
            align="justify"
            :breakpoint="0"
            class="listing__tabs text-accent"
            inline-label
            active-color="primary"
            indicator-color="primary"
            no-caps
          >
            <q-tab
              :label="`${ordinal(auctionRound.number, $i18n.locale)} ${t('section.auction.round')}`"
              :name="auctionRound.number"
              :ripple="false"
            />
          </q-tabs>
        </div>

        <q-separator />
      </div>

      <q-tab-panels v-model="tab" animated keep-alive>
        <q-tab-panel
          v-for="auctionRound in auctionRounds"
          :key="auctionRound.number"
          :name="auctionRound.number"
        >
          <div class="lp-auction-info__round-content">
            <div class="lp-auction-info__round-content-item">
              <div class="lp-auction-info__round-content-item-content">
                <span
                  class="lp-auction-info__round-content-label"
                  v-text="t('section.auction.startingPrice')"
                />
                <span
                  class="lp-auction-info__round-content-value"
                  v-text="formatPrice(auctionRound.startingPrice)"
                />
              </div>
              <q-separator />
            </div>

            <div class="lp-auction-info__round-content-item">
              <div class="lp-auction-info__round-content-item-content">
                <span
                  class="lp-auction-info__round-content-label"
                  v-text="t('section.auction.date')"
                />
                <span
                  class="lp-auction-info__round-content-value"
                  v-text="toDateFormat(auctionRound.auctionDate)"
                />
              </div>
              <q-separator />
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </div>

    <div v-if="showAuctionSiblings">
      <div
        class="lp-auction-info__separate-auction-label"
        v-text="t('section.auction.inSeparateAuction')"
      />

      <LPAuctionSiblings />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import LPAuctionSiblings from '@/components/ListingPage/fragments/LPAuctionSiblings.vue';
import { useTheme } from '@/composables/theme';
import { useVarPool } from '@/elr/listing_page/var_pool';
import translations from '@/i18n/translations/components/listingPage.json';
import useListingStore from '@/store/modules/listing';
import { ordinal } from '@/utils/number';
import { toDateFormat } from '@/utils/time';

const { formatPrice } = useTheme();

const { auctionRounds } = storeToRefs(useListingStore());
const { cans } = useVarPool();
const { t } = useI18n(translations);

const currentRound = computed(() => Math.max(...auctionRounds.value.map(round => round.number)));

const tab = ref(currentRound.value);

const showAuctionSiblings = computed(
  () => cans.value.includes('auction-siblings') && tab.value === currentRound.value
);
</script>

<style lang="scss">
@use 'sass:map';
@use '@/css/color_pallette' as c;

.lp-auction-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .q-tab-panel {
    padding: 0;
  }
}

.lp-auction-info__container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.lp-auction-info__tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-self: start;
}

.lp-auction-info__subtitle {
  font-size: 1.125rem;
  font-weight: 800;
  line-height: 1.6;
  color: c.$secondary;
}

.lp-auction-info__round-content {
  display: flex;

  @media (max-width: $breakpoint-md) {
    flex-direction: column;
  }
}

.lp-auction-info__round-content-item {
  flex-shrink: 0;
  flex-basis: 50%;

  &:not(:last-child) {
    padding-right: 1rem;
  }

  @media (max-width: $breakpoint-md) {
    flex-basis: 100%;

    &:not(:last-child) {
      padding-right: 0;
    }
  }
}

.lp-auction-info__round-content-item-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
}

.lp-auction-info__round-content-label {
  font-size: 0.875rem;
  font-weight: 800;
  line-height: 1.6;
  color: c.$secondary;
}

.lp-auction-info__round-content-value {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.6;
  color: c.$accent;
}

.lp-auction-info__separate-auction-label {
  margin-bottom: 16px;
  font-size: 0.875rem;
  font-weight: 800;
  line-height: 1.6;
  color: c.$secondary;
}
</style>
